import { createStore, del, get, keys, set } from 'idb-keyval'
import type { Project } from '@/model/types'
import type { ProjectSummary, StorageProvider } from './provider'

const store = createStore('house-builder', 'projects')
const PREFIX = 'project:'

/** Browser-local persistence via IndexedDB. The default, offline-capable provider. */
export class LocalIndexedDBProvider implements StorageProvider {
  readonly kind = 'local' as const
  readonly canWrite = true

  async list(): Promise<ProjectSummary[]> {
    const allKeys = (await keys(store)) as string[]
    const projectKeys = allKeys.filter((k) => typeof k === 'string' && k.startsWith(PREFIX))
    const summaries = await Promise.all(
      projectKeys.map(async (k) => {
        const p = (await get(k, store)) as Project | undefined
        if (!p) return null
        return { id: p.id, name: p.name, updatedAt: p.updatedAt }
      }),
    )
    return summaries
      .filter((s): s is ProjectSummary => s !== null)
      .sort((a, b) => b.updatedAt - a.updatedAt)
  }

  async load(id: string): Promise<Project | null> {
    const p = (await get(PREFIX + id, store)) as Project | undefined
    return p ?? null
  }

  async save(project: Project): Promise<void> {
    await set(PREFIX + project.id, project, store)
  }

  async remove(id: string): Promise<void> {
    await del(PREFIX + id, store)
  }
}

export const localProvider = new LocalIndexedDBProvider()
